import { useMemo } from 'react';
import type { VoidFunctionComponent } from 'react';

import { plural } from '../../../../../shared/helpers';
import { ExceptionIgnoreScope, ExceptionPackageScope } from '../../../../../shared/issuesV2';
import { IgnoredKind } from '../../../../../shared/issuesV2/common';
import Locator from '../../../../../shared/Locator';
import { Badge } from '../../../generic';
import Tooltip from '../../../generic/Tooltip';
import type { IgnoredKindMetadata } from '../../../../../shared/issuesV2/common';

import IssueItemBadge from './IssueItemBadge';

export type Props = {
  ignoredKinds: IgnoredKindMetadata[];
  numProjects: number;
};

const ignoreScopeMessages = {
  [ExceptionIgnoreScope.Project]: 'Ignored by project',
  [ExceptionIgnoreScope.ProjectAndReleaseGroups]: 'Ignored Proj + RG',
  [ExceptionIgnoreScope.ReleaseGroup]: 'Ignored by release group',
  [ExceptionIgnoreScope.Policy]: 'Ignored by policy',
  [ExceptionIgnoreScope.Organization]: 'Ignored globally',
};

/**
 * Ignored kind issue badge.
 */
const IgnoredKindBadge: VoidFunctionComponent<Props> = ({ ignoredKinds, numProjects }) => {
  const badges = useMemo(() => ignoredKinds.map((metadata) => {
    if (metadata.ignoreType === IgnoredKind.Manual) {
      return (
        <Badge color="light-gray" key="ignore-kind-badge">
          Manual
        </Badge>
      );
    }

    /*
     * When the projects list is empty, it's the global issues scope. IssueItem receives
     *   showProjectsList={scope.type === 'global'}
     * from IssuesTable, and then all of the CategoryIssueItem components have
     *   projects={showProjectsList ? issue.projects : []}
     * Therefore, we know it's global scope when the project list is empty.
     */
    const isGlobal = !!numProjects;

    let tooltipText = 'All versions';
    if (isGlobal) {
      tooltipText = `Ignored in ${numProjects} ${plural(numProjects, 'project')}`;
    } else if (metadata.packageScope === ExceptionPackageScope.SelectedVersion) {
      // We know the revisionLocator always exists if the packageScope is for a selected version.
      tooltipText = `vers. ${Locator.parse(metadata.revisionLocator as string).revision}`;
    }

    return (
      <Badge color="light-gray" key="ignore-kind-badge">
        <Tooltip id="ignored-kind-tooltip" tooltip={tooltipText}>
          <span>
            {ignoreScopeMessages[metadata.ignoreScope]}
          </span>
        </Tooltip>
      </Badge>
    );
  }), [ignoredKinds, numProjects]);

  return (
    <IssueItemBadge>
      {badges}
    </IssueItemBadge>
  );
};

export default IgnoredKindBadge;
